// frontend/src/api/timeline.ts
/**
 * API client for organism timeline entries.
 * Wraps the project's api client for /api/tanks/{tankId}/livestock/{livestockId}/timeline.
 */

import { api, apiClient } from './client';
import { buildApiUrl } from './config';
import { getLargePhoto } from './largePhoto';
import { RateLimitError } from './rateLimit';
import { getCachedSession } from '@/lib/supabase';
import type {
  ClientTakenAtSource,
  TimelineEntry,
  TimelineEntryUpdate,
  TimelineListResponse,
  TimelineListParams,
  MigrateNotesResponse,
} from '@/types/timeline';
import type { LargePhoto } from '@/types/tankPhotos';

export interface TimelineCreateWithPhoto {
  entry_text?: string | null;
  entry_date: string;
  photo?: File | null;
  target_livestock_id?: number;  // override which organism to attach to
  taken_at_source?: ClientTakenAtSource;  // set when entry_date is the photo's capture date
}

export const timelineApi = {
  /** The entry photo's large variant for the zoomed viewer; null when it has none. */
  large(tankId: string, livestockId: number, entryId: number): Promise<LargePhoto | null> {
    return getLargePhoto(
      `/api/tanks/${encodeURIComponent(tankId)}/livestock/${livestockId}/timeline/${entryId}/large`
    );
  },

  async list(
    tankId: string,
    livestockId: number,
    params: TimelineListParams = {},
  ): Promise<TimelineListResponse> {
    const searchParams = new URLSearchParams();
    if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
    if (params.offset !== undefined) searchParams.set('offset', String(params.offset));

    const query = searchParams.toString();
    const url = `/api/tanks/${tankId}/livestock/${livestockId}/timeline${query ? `?${query}` : ''}`;
    return api.get<TimelineListResponse>(url);
  },

  /**
   * Create a timeline entry with optional photo.
   *
   * Uses multipart/form-data to support file upload.
   * The apiClient is called directly (not via api.post) because
   * we need to send FormData, not JSON.
   */
  async create(
    tankId: string,
    livestockId: number,
    data: TimelineCreateWithPhoto,
    onProgress?: (percent: number) => void,
  ): Promise<TimelineEntry> {
    // Use target_livestock_id if provided, otherwise use the passed livestockId
    const effectiveLivestockId = data.target_livestock_id ?? livestockId;

    const formData = new FormData();
    formData.append('entry_date', data.entry_date);

    if (data.entry_text) {
      formData.append('entry_text', data.entry_text);
    }

    if (data.photo) {
      formData.append('photo', data.photo);
    }

    if (data.taken_at_source) {
      formData.append('taken_at_source', data.taken_at_source);
    }

    const url = `/api/tanks/${tankId}/livestock/${effectiveLivestockId}/timeline`;

    // Use XMLHttpRequest for upload progress support
    if (onProgress && data.photo) {
      return new Promise<TimelineEntry>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const fullUrl = buildApiUrl(url);

        xhr.open('POST', fullUrl);

        // Add auth header
        const session = getCachedSession();
        if (session?.access_token) {
          xhr.setRequestHeader('Authorization', `Bearer ${session.access_token}`);
        }

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            onProgress(percent);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else if (xhr.status === 429) {
            reject(new RateLimitError(xhr.getResponseHeader('Retry-After')));
          } else {
            try {
              const error = JSON.parse(xhr.responseText);
              reject(new Error(error.detail || `HTTP ${xhr.status}`));
            } catch {
              reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
            }
          }
        };

        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
      });
    }

    // For text-only entries or when progress tracking not needed,
    // use fetch via apiClient (simpler)
    const response = await apiClient(url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  async update(
    tankId: string,
    livestockId: number,
    entryId: number,
    data: TimelineEntryUpdate,
  ): Promise<TimelineEntry> {
    return api.put<TimelineEntry>(
      `/api/tanks/${tankId}/livestock/${livestockId}/timeline/${entryId}`,
      data,
    );
  },

  async delete(
    tankId: string,
    livestockId: number,
    entryId: number,
  ): Promise<void> {
    await api.delete(
      `/api/tanks/${tankId}/livestock/${livestockId}/timeline/${entryId}`,
    );
  },

  async migrateNotes(tankId: string, livestockId: number): Promise<MigrateNotesResponse> {
    return api.post<MigrateNotesResponse>(
      `/api/tanks/${tankId}/livestock/${livestockId}/timeline/migrate-notes`,
    );
  },
};
